<template>
  <v-card-text>
    <!-- Header informativo -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-alert
          type="info"
          prominent
          border="start"
          class="mb-4"
        >
          <v-icon slot="prepend">mdi-account-group</v-icon>
          <div class="text-h6">Incidencias del Equipo</div>
          <!-- P13-V7B (refutation): the rows are the zone incidences whose `reportedBy` is not the session's user. -->
          <div>Estas son las incidencias de los grupos que supervisas reportadas por otros usuarios. Puedes verlas, comentarlas y seguir su progreso.</div>
        </v-alert>
      </v-col>
    </v-row>

    <!-- Controles y filtros -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Buscar incidencias..."
          variant="outlined"
          density="compact"
          clearable
          @input="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Estado"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-select
          v-model="priorityFilter"
          :items="priorityOptions"
          label="Prioridad"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="3">
        <v-select
          v-model="employeeFilter"
          :items="employeeOptions"
          label="Empleado"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-btn
          @click="refreshData"
          :loading="loading"
          color="primary"
          variant="outlined"
          block
        >
          <v-icon left>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <!--
      P13-V7B (refutation) — Estadísticas rápidas: counts of the LISTED rows, in the server's own status
      vocabulary, and "Sin datos" until a list arrives (they used to start at 0 and stay 0 on failure).
      "Pendientes" counted `in_progress` too and "Resueltas" matched states the server never sends.
      "Urgentes" is RETIRED: it flagged `priority === 'alta'` older than 24 h — a Spanish key no row
      carries and a deadline no server states.
    -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-card class="stat-card text-center">
          <v-card-text>
            <v-avatar color="blue" size="48" class="mb-2">
              <v-icon color="white">mdi-format-list-bulleted</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold" data-testid="fs-team-listed">{{ displayStat(stats.listed) }}</div>
            <div class="text-caption text-grey">Listadas</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="stat-card text-center">
          <v-card-text>
            <v-avatar color="orange" size="48" class="mb-2">
              <v-icon color="white">mdi-clock</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold" data-testid="fs-team-open">{{ displayStat(stats.open) }}</div>
            <div class="text-caption text-grey">Abiertas (listadas)</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="stat-card text-center">
          <v-card-text>
            <v-avatar color="green" size="48" class="mb-2">
              <v-icon color="white">mdi-check-circle</v-icon>
            </v-avatar>
            <div class="text-h5 font-weight-bold" data-testid="fs-team-closed">{{ displayStat(stats.closed) }}</div>
            <div class="text-caption text-grey">Cerradas (listadas)</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Lista de incidencias -->
    <v-row v-if="loading">
      <v-col cols="12">
        <div class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4 text-h6">Cargando incidencias del equipo...</div>
        </div>
      </v-col>
    </v-row>

    <!-- P13-V7B (refutation): a failed read is said as such, not rendered as "No hay incidencias". -->
    <v-row v-else-if="loadError">
      <v-col cols="12">
        <v-alert type="warning" variant="tonal" data-testid="fs-team-load-error">
          {{ loadError }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else-if="filteredIncidents.length === 0">
      <v-col cols="12">
        <v-alert type="info" prominent>
          <v-icon slot="prepend">mdi-information</v-icon>
          <div class="text-h6">No hay incidencias reportadas</div>
          <div>No se encontraron incidencias reportadas por tu equipo que coincidan con los filtros seleccionados.</div>
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col
        v-for="incident in paginatedIncidents"
        :key="incident.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          class="incident-card"
          :class="getIncidentCardClass(incident)"
          @click="viewDetails(incident)"
        >
          <v-card-title class="d-flex align-center">
            <v-icon
              :color="getPriorityColor(incident.priority)"
              class="me-2"
            >
              {{ getPriorityIcon(incident.priority) }}
            </v-icon>
            <span class="text-truncate">{{ incident.title }}</span>
            <v-spacer></v-spacer>
            <v-chip
              :color="getStatusColor(incident.status)"
              size="small"
              label
            >
              {{ getStatusText(incident.status) }}
            </v-chip>
          </v-card-title>

          <v-card-subtitle>
            <div class="d-flex align-center mb-1">
              <v-icon size="16" class="me-1">mdi-clock</v-icon>
              {{ formatDate(incident.createdAt) }}
            </div>
            <div class="d-flex align-center">
              <v-icon size="16" class="me-1">mdi-map-marker</v-icon>
              <span class="text-truncate">{{ incident.point?.address || incident.point?.name || 'Sin dirección' }}</span>
            </div>
          </v-card-subtitle>

          <v-card-text>
            <p class="text-body-2 text-truncate mb-2">{{ incident.description }}</p>
            
            <div class="d-flex align-center justify-space-between">
              <v-chip
                :color="getCategoryColor(incident.category)"
                size="small"
                variant="outlined"
              >
                {{ incident.category || 'Sin categoría' }}
              </v-chip>
              
              <div class="text-caption text-grey">
                ID: #{{ incident.id }}
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn
              size="small"
              variant="text"
              @click.stop="viewDetails(incident)"
            >
              <v-icon left size="16">mdi-eye</v-icon>
              Ver Detalles
            </v-btn>

            <!-- Badge de departamento pendiente -->
            <v-chip
              v-if="!incident.assignedDepartmentId"
              color="warning"
              size="x-small"
              variant="tonal"
              class="mx-2"
            >
              <v-icon size="12" class="mr-1">mdi-alert-circle</v-icon>
              Pendiente Asignar
            </v-chip>

            <v-spacer></v-spacer>

            <!--
              W2 · the "Asignar Depto" trigger is REMOVED from the signed incident surface.

              Department assignment/transfer/escalation is signed only IF B0B confirms, and that
              condition is currently UNMET. The dialog it opened reads
              `GET /api/field-supervisor/departments`, one of the 38 role-home siblings the W2
              perimeter now contains to vendor-only, so the control would offer a field supervisor an
              action whose first request answers 403.

              ONLY THE TRIGGER IS REMOVED. `AssignDepartmentDialog` still exists, and the generic
              `PUT /api/incidences/:id/assign-department` writer it calls is untouched: that
              authority has its own owner, and this mission neither activates nor closes B0B.
            -->

            <v-chip
              :color="getPriorityColor(incident.priority)"
              size="x-small"
              variant="flat"
            >
              {{ incident.priority }}
            </v-chip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Paginación -->
    <v-row v-if="filteredIncidents.length > itemsPerPage" class="mt-4">
      <v-col cols="12">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
        />
      </v-col>
    </v-row>

    <!-- Dialog de detalles - Diseño profesional -->
    <v-dialog 
      v-model="showDetailsDialog" 
      max-width="900px"
      scrollable
      @click:outside="showDetailsDialog = false"
      @keydown.esc="showDetailsDialog = false"
    >
      <v-card v-if="selectedIncident" class="incident-details-dialog">
        <!-- Header mejorado -->
        <v-card-title class="d-flex align-center pa-4">
          <v-icon
            :icon="getPriorityIcon(selectedIncident.priority)"
            :color="getPriorityColor(selectedIncident.priority)"
            size="28"
            class="mr-3"
          ></v-icon>
          <div class="flex-grow-1">
            <h2 class="text-h6 mb-1">
              Detalles de Incidencia #{{ selectedIncident.id }}
            </h2>
            <p class="text-caption text-grey mb-0">
              {{ selectedIncident.title }}
            </p>
          </div>
          <v-chip
            :color="getStatusColor(selectedIncident.status)"
            variant="tonal"
            class="mr-3"
          >
            <v-icon 
              :icon="getStatusIcon(selectedIncident.status)" 
              size="16" 
              class="mr-1"
            ></v-icon>
            {{ getStatusText(selectedIncident.status) }}
          </v-chip>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showDetailsDialog = false"
          ></v-btn>
        </v-card-title>

        <!-- Contenido organizado -->
        <v-card-text class="pa-0">
          <v-container class="py-6">
            <v-row>
              <!-- Columna principal -->
              <v-col cols="12" md="8">
                <!-- Descripción -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-text-long" class="mr-2"></v-icon>
                    Descripción
                  </v-card-title>
                  <v-card-text>
                    <p class="text-body-1">{{ selectedIncident.description }}</p>
                  </v-card-text>
                </v-card>

                <!-- Ubicación -->
                <v-card v-if="selectedIncident.point" flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-map-marker" class="mr-2"></v-icon>
                    Ubicación
                  </v-card-title>
                  <v-card-text>
                    <div class="mb-2">
                      <strong>{{ selectedIncident.point.name }}</strong>
                    </div>
                    <div class="text-body-2 text-grey">
                      {{ selectedIncident.point.address || 'Sin dirección especificada' }}
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Comentarios -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2 d-flex justify-space-between align-center">
                    <div>
                      <v-icon icon="mdi-comment-multiple" class="mr-2"></v-icon>
                      Comentarios ({{ selectedIncident.comments?.filter(c => !c.isDeleted).length || 0 }})
                    </div>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="addComment(selectedIncident)"
                    >
                      Agregar
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <!-- Banner de respuesta/edición -->
                    <v-alert
                      v-if="replyingTo || editingComment"
                      :type="editingComment ? 'info' : 'success'"
                      variant="tonal"
                      closable
                      class="mb-3"
                      @click:close="cancelCommentAction"
                    >
                      <div class="d-flex align-center">
                        <v-icon 
                          :icon="editingComment ? 'mdi-pencil' : 'mdi-reply'" 
                          class="mr-2"
                        ></v-icon>
                        <div>
                          <div class="text-subtitle-2">
                            {{ editingComment ? 'Editando comentario' : `Respondiendo a ${replyingTo?.userName}` }}
                          </div>
                          <div class="text-caption text-grey">
                            {{ (editingComment ? editingComment.comment : replyingTo?.comment || '').substring(0, 80) }}...
                          </div>
                        </div>
                      </div>
                    </v-alert>

                    <div v-if="!selectedIncident.comments || selectedIncident.comments.length === 0" 
                         class="text-center text-grey py-4">
                      No hay comentarios aún
                    </div>
                    <div v-else class="comments-list">
                      <v-card
                        v-for="comment in selectedIncident.comments"
                        :key="comment.id"
                        variant="outlined"
                        :class="['mb-3', { 'deleted-comment': comment.isDeleted }]"
                      >
                        <v-card-text class="pa-3">
                          <!-- Preview de respuesta -->
                          <div v-if="comment.replyTo" class="reply-to-preview mb-2 pa-2 rounded" style="background: rgba(0,0,0,0.05)">
                            <v-icon icon="mdi-reply" size="14" class="mr-1"></v-icon>
                            <span class="text-caption">
                              <strong>{{ comment.replyTo.userName }}</strong>: 
                              {{ comment.replyTo.comment }}
                            </span>
                          </div>

                          <div class="d-flex justify-space-between align-center mb-2">
                            <div class="d-flex align-center">
                              <v-avatar size="24" color="primary" class="mr-2">
                                <v-icon :icon="comment.isDeleted ? 'mdi-account-off' : 'mdi-account'" size="14"></v-icon>
                              </v-avatar>
                              <span class="font-weight-medium">{{ comment.userName || 'Usuario' }}</span>
                            </div>
                            <div class="d-flex align-center">
                              <span class="text-caption text-grey mr-2">
                                {{ formatDate(comment.createdAt || comment.created_at || comment.timestamp) }}
                                <span v-if="comment.isEdited && !comment.isDeleted" class="ml-1">(editado)</span>
                              </span>
                              <!-- Menú de acciones -->
                              <v-menu v-if="canModifyComment(comment)">
                                <template v-slot:activator="{ props }">
                                  <v-btn
                                    icon="mdi-dots-vertical"
                                    variant="text"
                                    size="x-small"
                                    v-bind="props"
                                  ></v-btn>
                                </template>
                                <v-list>
                                  <v-list-item 
                                    v-if="!comment.isDeleted && canEditComment(comment)"
                                    @click="startEditComment(comment)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-pencil" color="primary"></v-icon>
                                    </template>
                                    <v-list-item-title>Editar</v-list-item-title>
                                  </v-list-item>
                                  <v-list-item 
                                    v-if="!comment.isDeleted"
                                    @click="startReplyComment(comment)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-reply" color="info"></v-icon>
                                    </template>
                                    <v-list-item-title>Responder</v-list-item-title>
                                  </v-list-item>
                                  <v-list-item 
                                    v-if="canDeleteComment(comment)"
                                    @click="deleteComment(comment.id)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-delete" color="error"></v-icon>
                                    </template>
                                    <v-list-item-title>Eliminar</v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </div>
                          </div>
                          <p :class="['mb-0 text-body-2', { 'text-grey': comment.isDeleted }]">
                            {{ comment.comment }}
                          </p>
                        </v-card-text>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Sidebar derecho -->
              <v-col cols="12" md="4">
                <!-- Información general -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-information" class="mr-2"></v-icon>
                    Información General
                  </v-card-title>
                  <v-card-text>
                    <div class="info-list">
                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Prioridad</div>
                        <v-chip
                          :color="getPriorityColor(selectedIncident.priority)"
                          size="small"
                          variant="tonal"
                        >
                          <v-icon 
                            :icon="getPriorityIcon(selectedIncident.priority)" 
                            size="14" 
                            class="mr-1"
                          ></v-icon>
                          {{ getPriorityText(selectedIncident.priority) }}
                        </v-chip>
                      </div>
                      
                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Categoría</div>
                        <v-chip
                          :color="getCategoryColor(selectedIncident.category)"
                          size="small"
                          variant="tonal"
                        >
                          {{ selectedIncident.category || 'Sin categoría' }}
                        </v-chip>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Fecha de Reporte</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-calendar" size="16" class="mr-1"></v-icon>
                          {{ formatDate(selectedIncident.createdAt) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Última Actualización</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-update" size="16" class="mr-1"></v-icon>
                          {{ formatDate(selectedIncident.updatedAt) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Tiempo Transcurrido</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-clock-outline" size="16" class="mr-1"></v-icon>
                          {{ getElapsedTime(selectedIncident.createdAt) }}
                        </div>
                      </div>

                      <div class="info-item">
                        <div class="text-caption text-grey mb-1">ID de Incidencia</div>
                        <div class="text-body-2 font-weight-medium">
                          #{{ selectedIncident.id }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Asignación -->
                <v-card flat elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-account-tie" class="mr-2"></v-icon>
                    Asignación
                  </v-card-title>
                  <v-card-text>
                    <div class="text-body-2">
                      {{ selectedIncident.assignedTo ? `Asignado a: ${selectedIncident.assignedTo}` : 'Sin asignar' }}
                    </div>
                    <div v-if="selectedIncident.reportedBy" class="text-caption text-grey mt-2">
                      Reportado por: {{ selectedIncident.reportedBy }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon icon="mdi-alert-circle-outline" color="error" class="mr-2"></v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-0">
            ¿Estás seguro de que deseas eliminar este seguimiento?
          </p>
          <p class="text-caption text-grey mt-2">
            Esta acción no se puede deshacer. El comentario se mostrará como "Se eliminó este mensaje".
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            variant="text" 
            @click="showDeleteDialog = false; commentToDelete = null"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="error" 
            variant="flat"
            :loading="deletingComment"
            @click="confirmDeleteComment"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para añadir/editar/responder comentario -->
    <v-dialog v-model="showCommentDialog" max-width="500px">
      <v-card v-if="selectedIncident">
        <v-card-title>
          <v-icon left>{{ editingComment ? 'mdi-pencil' : replyingTo ? 'mdi-reply' : 'mdi-comment-plus' }}</v-icon>
          {{ editingComment ? 'Editar Seguimiento' : replyingTo ? 'Responder Seguimiento' : 'Añadir Seguimiento' }}
        </v-card-title>
        <v-card-subtitle>
          Incidencia: {{ selectedIncident.title }}
        </v-card-subtitle>
        <v-card-text>
          <!-- Banner de respuesta -->
          <v-alert
            v-if="replyingTo"
            type="success"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="cancelCommentAction"
          >
            <div class="d-flex align-center">
              <v-icon icon="mdi-reply" class="mr-2"></v-icon>
              <div>
                <div class="text-subtitle-2">Respondiendo a {{ replyingTo.userName }}</div>
                <div class="text-caption text-grey">
                  {{ replyingTo.comment.substring(0, 80) }}...
                </div>
              </div>
            </div>
          </v-alert>

          <!-- Banner de edición -->
          <v-alert
            v-if="editingComment"
            type="info"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="cancelCommentAction"
          >
            <div class="d-flex align-center">
              <v-icon icon="mdi-pencil" class="mr-2"></v-icon>
              <div>
                <div class="text-subtitle-2">Editando seguimiento</div>
              </div>
            </div>
          </v-alert>

          <v-textarea
            v-model="newComment"
            :label="editingComment ? 'Editar seguimiento' : 'Tu seguimiento'"
            rows="4"
            variant="outlined"
            counter="500"
            :rules="[v => !!v || 'El comentario es requerido', v => v.length <= 500 || 'Máximo 500 caracteres']"
            :placeholder="editingComment ? '' : 'Escribe tu seguimiento sobre esta incidencia reportada por tu equipo...'"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showCommentDialog = false; cancelCommentAction()">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="submittingComment"
            :disabled="!newComment.trim()"
            @click="submitComment"
          >
            {{ editingComment ? 'Guardar' : 'Enviar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- W2: the AssignDepartmentDialog mount is removed with its trigger (B0B unmet). The
         component file is retained for the owner that activates B0B. -->

  </v-card-text>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import SupervisorService from '@/services/supervisorService'
import { useAuthStore } from '@/stores/auth'
// W2 LIFECYCLE-SIBLING (client truth): one shared mirror of the merged backend allowlist.
import { canUseLifecycleSiblingWrites } from '@/config/lifecycleSiblingPerimeter'
import type { Incident } from '@/types'

// ================== PROPS ==================
interface Props {
  supervisorId: number
}

const props = defineProps<Props>()
// W2 LIFECYCLE-SIBLING: identity source for the contained comment edit/delete capability.
const auth = useAuthStore()

// ================== EMITS ==================
const emit = defineEmits<{
  'refresh-stats': []
}>()

// ================== ESTADO REACTIVO ==================
const loading = ref(false)
const submittingComment = ref(false)
const incidents = ref<Incident[]>([])
const filteredIncidents = ref<Incident[]>([])

// Filtros
const searchQuery = ref('')
const statusFilter = ref('')
const priorityFilter = ref('')
const employeeFilter = ref('')

// Paginación
const currentPage = ref(1)
const itemsPerPage = ref(8)

// Dialogs
const showDetailsDialog = ref(false)
const showCommentDialog = ref(false)
const selectedIncident = ref<Incident | null>(null)
const newComment = ref('')

// Dialogs de asignación de departamento
// W2: the department-assignment dialog state was removed with its trigger (B0B unmet).

// Opciones para filtros
// P13-V7B (refutation): the filters compare against the row's own `status` / `priority`, so they offer
// the server's vocabulary. The Spanish values ('abierta', 'alta', …) matched no row: every choice
// emptied the list.
const statusOptions = [
  { title: 'Pendiente', value: 'pending' },
  { title: 'En Progreso', value: 'in_progress' },
  { title: 'Pendiente de validación', value: 'pending_validation' },
  { title: 'Cerrada', value: 'closed' },
  { title: 'Cancelada', value: 'cancelled' }
]

const priorityOptions = [
  { title: 'Crítica', value: 'critical' },
  { title: 'Alta', value: 'high' },
  { title: 'Media', value: 'medium' },
  { title: 'Baja', value: 'low' }
]

const employeeOptions = ref<Array<{title: string, value: any}>>([])

/** P13-V7B (refutation): set only once the server answered — until then no count is published. */
const loaded = ref(false)
const loadError = ref('')
const statusOf = (incident: Incident) => String((incident as any)?.status || '').toLowerCase()

// ================== COMPUTED ==================
const totalPages = computed(() => Math.ceil(filteredIncidents.value.length / itemsPerPage.value))

const paginatedIncidents = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredIncidents.value.slice(start, end)
})

/**
 * P13-V7B (refutation) — counts of the LISTED rows in the canonical vocabulary (open = pending +
 * in_progress + pending_validation; closed = closed), or `null` before a list was received.
 */
const stats = computed(() => {
  if (!loaded.value) return { listed: null as number | null, open: null as number | null, closed: null as number | null }
  return {
    listed: incidents.value.length,
    open: incidents.value.filter((i) => ['pending', 'in_progress', 'pending_validation'].includes(statusOf(i))).length,
    closed: incidents.value.filter((i) => statusOf(i) === 'closed').length
  }
})

const displayStat = (value: number | null) => (typeof value === 'number' ? value : 'Sin datos')

// ================== MÉTODOS ==================
/**
 * Carga las incidencias reportadas por empleados
 */
const loadIncidents = async () => {
  if (!props.supervisorId) return

  try {
    loading.value = true
    const response = await SupervisorService.getEmployeeIncidents(props.supervisorId)
    // P13-V7B (refutation): only a LIST is a list — anything else is a failed read, never "0".
    if (!Array.isArray(response)) throw new Error('La respuesta de incidencias del equipo no es una lista.')
    incidents.value = response
    loaded.value = true
    loadError.value = ''

    // Extraer empleados únicos para el filtro
    // P13-V7B (refutation): the row carries the reporter's user id, not a name — it is labelled as such.
    const employees = [...new Set(response.map(i => (i as any).reportedBy).filter(Boolean))]
    employeeOptions.value = employees.map(emp => ({ title: `Usuario #${emp}`, value: emp }))

    applyFilters()
  } catch (error: any) {
    console.error('Error loading employee incidents:', error)
    incidents.value = []
    filteredIncidents.value = []
    loaded.value = false
    loadError.value = error?.message || 'No fue posible cargar las incidencias del equipo.'
  } finally {
    loading.value = false
  }
}

/**
 * Aplica filtros a las incidencias
 */
const applyFilters = () => {
  let filtered = [...incidents.value]

  // Filtro de búsqueda por texto
  // P13-V7B (refutation): null-safe. Rows carry no `category` and a numeric `reportedBy`, so the search
  // box threw a TypeError on the first keystroke instead of filtering.
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    const has = (value: unknown) => String(value ?? '').toLowerCase().includes(query)
    filtered = filtered.filter(incident =>
      has(incident.title) ||
      has(incident.description) ||
      has((incident as any).category) ||
      has((incident as any).reportedBy)
    )
  }

  // Filtro por estado
  if (statusFilter.value) {
    filtered = filtered.filter(incident => incident.status === statusFilter.value)
  }

  // Filtro por prioridad
  if (priorityFilter.value) {
    filtered = filtered.filter(incident => incident.priority === priorityFilter.value)
  }

  // Filtro por empleado
  if (employeeFilter.value) {
    filtered = filtered.filter(incident => incident.reportedBy === employeeFilter.value)
  }

  filteredIncidents.value = filtered
  currentPage.value = 1
}

/**
 * Actualiza los datos
 */
const refreshData = () => {
  loadIncidents()
  emit('refresh-stats')
}

/**
 * Ver detalles de la incidencia
 */
const viewDetails = async (incident: Incident) => {
  try {
    const response = await SupervisorService.getIncidentDetails(props.supervisorId, incident.id)
    selectedIncident.value = response
    showDetailsDialog.value = true
  } catch (error) {
    console.error('Error loading incident details:', error)
  }
}

/**
 * Añadir comentario
 */
const addComment = (incident: Incident) => {
  selectedIncident.value = incident
  newComment.value = ''
  showCommentDialog.value = true
}

/**
 * Envía el comentario
 */
const submitComment = async () => {
  if (!selectedIncident.value || !newComment.value.trim()) return
  
  try {
    submittingComment.value = true
    
    // P13-V6B@V1 — ruta canónica de comments (las firmas exigen el incidentId del padre).
    if (editingComment.value) {
      // Editar comentario existente
      // W2 LIFECYCLE-SIBLING: adversarial guard — the dispatcher is reachable without the menu item.
      if (!canUseLifecycleSiblingWrites(auth.userRole)) return
      await SupervisorService.editIncidentComment(
        selectedIncident.value.id,
        editingComment.value.id,
        newComment.value.trim()
      )
    } else {
      // Añadir nuevo comentario o respuesta
      await SupervisorService.addIncidentComment(
        selectedIncident.value.id,
        newComment.value.trim(),
        replyingTo.value?.id
      )
    }
    
    showCommentDialog.value = false
    cancelCommentAction()
    
    // Recargar los detalles de la incidencia para mostrar el nuevo comentario
    const updatedIncident = await SupervisorService.getIncidentDetails(props.supervisorId, selectedIncident.value.id)
    selectedIncident.value = updatedIncident
    
    // Recargar lista completa
    loadIncidents()
    emit('refresh-stats')
  } catch (error) {
    console.error('Error adding/editing comment:', error)
  } finally {
    submittingComment.value = false
  }
}

// Estado para respuestas y edición
const replyingTo = ref<any>(null)
const editingComment = ref<any>(null)

/**
 * Inicia el modo de respuesta a un comentario
 */
const startReplyComment = (comment: any) => {
  replyingTo.value = comment
  editingComment.value = null
  newComment.value = ''
  showCommentDialog.value = true
}

/**
 * Inicia el modo de edición de un comentario
 */
const startEditComment = (comment: any) => {
  editingComment.value = comment
  replyingTo.value = null
  newComment.value = comment.comment
  showCommentDialog.value = true
}

/**
 * Cancela la acción de respuesta o edición
 */
const cancelCommentAction = () => {
  replyingTo.value = null
  editingComment.value = null
  newComment.value = ''
}

// W2 · ROLE-HOME-API-FIELD-SUPERVISOR — the department-assignment trigger, its dialog mount, its
// state and this success callback were removed together. Leaving the handler would be dead code
// whose only caller was the dialog; leaving the state would keep the capability half-wired.
// `AssignDepartmentDialog.vue` and the generic `PUT /api/incidences/:id/assign-department` writer
// are deliberately untouched, for the owner that activates B0B.

// Estado para confirmación de eliminación
const showDeleteDialog = ref(false)
const commentToDelete = ref<number | null>(null)
const deletingComment = ref(false)

/**
 * Inicia el proceso de eliminación de comentario
 */
const deleteComment = (commentId: number) => {
  commentToDelete.value = commentId
  showDeleteDialog.value = true
}

/**
 * Confirma y ejecuta la eliminación del comentario
 */
const confirmDeleteComment = async () => {
  // P13-V6B@V1 — la ruta canónica de delete exige el incidentId del padre.
  if (!commentToDelete.value || !selectedIncident.value) return

  try {
    // W2 LIFECYCLE-SIBLING: adversarial guard — the dispatcher is reachable without the menu item.
    if (!canUseLifecycleSiblingWrites(auth.userRole)) return
    deletingComment.value = true
    await SupervisorService.deleteIncidentComment(selectedIncident.value.id, commentToDelete.value)
    
    showDeleteDialog.value = false
    commentToDelete.value = null
    
    // Recargar detalles de la incidencia
    if (selectedIncident.value) {
      const updatedIncident = await SupervisorService.getIncidentDetails(props.supervisorId, selectedIncident.value.id)
      selectedIncident.value = updatedIncident
    }
    
    loadIncidents()
    emit('refresh-stats')
  } catch (error) {
    console.error('Error deleting comment:', error)
    alert('Error al eliminar comentario')
  } finally {
    deletingComment.value = false
  }
}

/**
 * Verifica si el usuario puede editar el comentario
 */
const canEditComment = (comment: any) => {
  // W2 LIFECYCLE-SIBLING: comment EDIT is contained to vendor/conditional roles. This page is
  // admitted to `field_supervisor` only, so for every identity that can actually mount it the
  // capability is withdrawn — the authorship rule below never gets the chance to re-admit it.
  if (!canUseLifecycleSiblingWrites(auth.userRole)) return false
  return !comment.isDeleted && comment.userId === props.supervisorId
}

/**
 * Verifica si el usuario puede eliminar el comentario
 */
const canDeleteComment = (comment: any) => {
  // W2 LIFECYCLE-SIBLING: comment DELETE is contained to vendor/conditional roles.
  if (!canUseLifecycleSiblingWrites(auth.userRole)) return false
  if (comment.isDeleted) return false
  return comment.userId === props.supervisorId
}

/**
 * Verifica si se debe mostrar el menú de acciones
 */
const canModifyComment = (comment: any) => {
  return canDeleteComment(comment) || canEditComment(comment) || !comment.isDeleted
}

// P13-V7B (refutation): `isUrgent`, `getTimeProgressColor` and `getTimeProgressValue` were REMOVED.
// They judged rows against 24/72/168-hour limits written here, keyed on 'alta'/'media' — a vocabulary
// the server never sends — so "Urgentes" was always 0 and the progress helpers had no reader.

/**
 * Obtener horas transcurridas desde una fecha
 */
const getHoursSince = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now.getTime() - date.getTime())
  return diffTime / (1000 * 60 * 60)
}

/**
 * Obtener tiempo transcurrido en formato legible
 */
const getTimeElapsed = (dateString: string) => {
  const hours = getHoursSince(dateString)
  if (hours < 1) return 'Hace minutos'
  if (hours < 24) return `${Math.floor(hours)} horas`
  const days = Math.floor(hours / 24)
  if (days === 1) return '1 día'
  if (days < 30) return `${days} días`
  const months = Math.floor(days / 30)
  return months === 1 ? '1 mes' : `${months} meses`
}

/**
 * Obtiene el color según la prioridad
 * P13-V7B (refutation): keyed on the server's vocabulary; the Spanish keys never matched a row.
 */
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red-darken-2'
    case 'high': return 'red'
    case 'medium': return 'orange'
    case 'low': return 'green'
    default: return 'grey'
  }
}

/**
 * Obtiene el ícono según la prioridad
 */
const getPriorityIcon = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'mdi-alert-octagon'
    case 'high': return 'mdi-alert'
    case 'medium': return 'mdi-alert-outline'
    case 'low': return 'mdi-information'
    default: return 'mdi-help-circle'
  }
}

/**
 * Obtiene el color según el estado
 * P13-V7B (refutation): canonical states; every row used to read "Desconocido".
 */
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'red'
    case 'in_progress': return 'orange'
    case 'pending_validation': return 'purple'
    case 'closed': return 'green'
    case 'cancelled': return 'grey'
    default: return 'grey'
  }
}

/**
 * Obtiene el texto del estado
 */
const getStatusText = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'Pendiente'
    case 'in_progress': return 'En Progreso'
    case 'pending_validation': return 'Pendiente de validación'
    case 'closed': return 'Cerrada'
    case 'cancelled': return 'Cancelada'
    default: return 'Desconocido'
  }
}

/**
 * Obtiene el color de la categoría
 */
const getCategoryColor = (category: string) => {
  if (!category) return 'grey'
  const colors = ['blue', 'purple', 'teal', 'indigo', 'cyan']
  const hash = category.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Obtiene la clase CSS para la tarjeta de incidencia
 */
const getIncidentCardClass = (incident: Incident) => {
  // P13-V7B (refutation): no 'urgent' class — there is no deadline to be urgent against.
  return ['employee-incident', `priority-${String(incident.priority || '').toLowerCase()}`].join(' ')
}

/**
 * Obtiene las iniciales de un nombre
 */
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

/**
 * Formatea la fecha
 */
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    console.warn('⚠️ formatDate: fecha vacía o null')
    return 'N/A'
  }
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      console.warn('⚠️ formatDate: fecha inválida:', dateString)
      return 'Fecha inválida'
    }
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('❌ Error formateando fecha:', error)
    return 'Error en fecha'
  }
}

/**
 * Obtiene el ícono según el estado
 */
const getStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'mdi-clock'
    case 'in_progress': return 'mdi-progress-clock'
    // P13-V7B (refutation): canonical states — `resolved` is not one.
    case 'pending_validation': return 'mdi-clipboard-check-outline'
    case 'closed': return 'mdi-check-circle'
    case 'cancelled': return 'mdi-close-circle'
    default: return 'mdi-help-circle'
  }
}

/**
 * Obtiene el texto de prioridad en español
 */
const getPriorityText = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'Crítica'
    case 'high': return 'Alta'
    case 'medium': return 'Media'
    case 'low': return 'Baja'
    default: return 'Sin prioridad'
  }
}

/**
 * Calcula el tiempo transcurrido desde la creación
 */
const getElapsedTime = (createdAt: string) => {
  const now = new Date()
  const created = new Date(createdAt)
  const diffMs = now.getTime() - created.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffDays > 0) {
    return `${diffDays} día${diffDays !== 1 ? 's' : ''}`
  } else if (diffHours > 0) {
    return `${diffHours} hora${diffHours !== 1 ? 's' : ''}`
  } else {
    return `${diffMins} minuto${diffMins !== 1 ? 's' : ''}`
  }
}

// ================== CICLO DE VIDA ==================
onMounted(() => {
  loadIncidents()
})

// ================== EXPOSICIÓN ==================
defineExpose({
  refreshData
})
</script>

<style scoped>
.incident-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.incident-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-card {
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

/* Dialog profesional */
.incident-details-dialog .info-list {
  display: flex;
  flex-direction: column;
}

.incident-details-dialog .info-item {
  padding: 8px 0;
}

.incident-details-dialog .comments-list {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.incident-details-dialog .comments-list::-webkit-scrollbar {
  width: 6px;
}

.incident-details-dialog .comments-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.incident-details-dialog .comments-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

/* Estilos de comentarios */
.deleted-comment {
  opacity: 0.6;
  background-color: rgba(0, 0, 0, 0.02);
}

.reply-to-preview {
  font-style: italic;
  border-left: 3px solid rgba(0, 0, 0, 0.2);
}

.incident-details-dialog .comments-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Prioridad borders */
.incident-card.priority-critical {
  border-left-color: #d32f2f;
}

.incident-card.priority-high {
  border-left-color: #f57c00;
}

.incident-card.priority-medium {
  border-left-color: #fbc02d;
}

.incident-card.priority-low {
  border-left-color: #388e3c;
}

.detail-item {
  margin-bottom: 8px;
  line-height: 1.5;
}

.comments-container {
  max-height: 400px;
  overflow-y: auto;
}

.map-placeholder {
  height: 300px;
  background-color: #f5f5f5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>
